import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { LAYANAN_CARE_TYPES, LAYANAN_CONDITIONS, LAYANAN_DESTINATIONS, today } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Ambulance, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

const dayFromDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("id-ID", { weekday: "long" });
};

const emptyForm = {
  patient_name: "",
  desa: "",
  dukuh: "",
  rt: "",
  rw: "",
  date: today(),
  time: "",
  destination: "rs",
  destination_detail: "",
  care_type: "rawat_jalan",
  patient_condition: "berbaring",
  family_phone: "",
};

export default function LayananAmbulance() {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.patient_name || !form.date || !form.family_phone) {
      toast.error("Nama pasien, tanggal, dan No. WA keluarga wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/public/service-requests", { ...form, day: dayFromDate(form.date) });
      toast.success("Permohonan layanan berhasil dikirim");
      const sent = (data.wa_notifs || []).filter((w) => w.ok).length;
      if (sent > 0) toast.success(`Notifikasi WA terkirim ke ${sent} nomor`);
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="layanan-ambulance-page">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link to="/"><img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" /></Link>
          <div className="flex-1">
            <p className="font-heading font-bold text-slate-900 leading-tight">Ambulance / MLU</p>
            <p className="text-xs text-slate-500">NU Care LAZISNU Dawe — Gratis</p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="layanan-ambulance-back-button">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Kembali
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        {success ? (
          <Card data-testid="layanan-ambulance-success">
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <CheckCircle2 className="h-14 w-14 text-emerald-700" />
              <p className="font-heading text-xl font-bold text-slate-800">Permohonan Terkirim</p>
              <p className="text-sm text-slate-500 max-w-md">
                Permohonan layanan Anda sudah masuk dan diteruskan ke Koordinator Layanan kami via WhatsApp. Petugas akan menghubungi Anda. Terima kasih.
              </p>
              <Link to="/">
                <Button className="mt-2 bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="layanan-ambulance-success-back">Kembali ke Beranda</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="layanan-ambulance-form-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Ambulance className="h-5 w-5 text-emerald-800" />
                Form Permohonan Layanan
              </CardTitle>
              <p className="text-xs text-slate-500">Layanan antar pasien gratis NU Care LAZISNU Dawe — permohonan diteruskan via WhatsApp ke Koordinator Layanan</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="layanan-ambulance-form">
                <div className="space-y-2">
                  <Label>Nama Pasien *</Label>
                  <Input data-testid="ambulance-name-input" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Desa</Label>
                  <Input data-testid="ambulance-desa-input" value={form.desa} onChange={(e) => setForm({ ...form, desa: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Dukuh</Label>
                  <Input data-testid="ambulance-dukuh-input" value={form.dukuh} onChange={(e) => setForm({ ...form, dukuh: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>RT</Label>
                  <Input data-testid="ambulance-rt-input" value={form.rt} onChange={(e) => setForm({ ...form, rt: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>RW</Label>
                  <Input data-testid="ambulance-rw-input" value={form.rw} onChange={(e) => setForm({ ...form, rw: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>No. WA Keluarga *</Label>
                  <Input data-testid="ambulance-phone-input" placeholder="08xxxxxxxxxx" value={form.family_phone} onChange={(e) => setForm({ ...form, family_phone: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Tanggal *</Label>
                  <Input type="date" data-testid="ambulance-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Hari</Label>
                  <Input data-testid="ambulance-day-input" value={dayFromDate(form.date)} readOnly className="bg-slate-50" />
                </div>
                <div className="space-y-2">
                  <Label>Jam</Label>
                  <Input type="time" data-testid="ambulance-time-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Tujuan</Label>
                  <Select value={form.destination} onValueChange={(v) => setForm({ ...form, destination: v })}>
                    <SelectTrigger data-testid="ambulance-destination-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LAYANAN_DESTINATIONS.map((d) => (
                        <SelectItem key={d.key} value={d.key} data-testid={`ambulance-destination-${d.key}`}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nama RS/Klinik/Tujuan</Label>
                  <Input data-testid="ambulance-destination-detail-input" placeholder="cth: RSUD Loekmono Hadi" value={form.destination_detail} onChange={(e) => setForm({ ...form, destination_detail: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Keterangan</Label>
                  <Select value={form.care_type} onValueChange={(v) => setForm({ ...form, care_type: v })}>
                    <SelectTrigger data-testid="ambulance-caretype-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LAYANAN_CARE_TYPES.map((c) => (
                        <SelectItem key={c.key} value={c.key} data-testid={`ambulance-caretype-${c.key}`}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Kondisi Pasien</Label>
                  <Select value={form.patient_condition} onValueChange={(v) => setForm({ ...form, patient_condition: v })}>
                    <SelectTrigger data-testid="ambulance-condition-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LAYANAN_CONDITIONS.map((c) => (
                        <SelectItem key={c.key} value={c.key} data-testid={`ambulance-condition-${c.key}`}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button type="submit" data-testid="ambulance-submit-button" disabled={saving} className="w-full bg-emerald-800 hover:bg-emerald-700 text-white">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim Permohonan"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
